import { useState } from 'react';
import AdminSidebar from '@/components/AdminSidebar';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { Menu, Save, Loader2 } from 'lucide-react';
import { useQuery, useQueryClient } from 'react-query';
import { getSiteConfig, updateSiteConfig, type SiteConfig } from '@/api/cmsService';
import { useToast } from '@/components/ui/use-toast';

export default function CmsSiteSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [localValues, setLocalValues] = useState<Record<string, string>>({});

  const { data: configs = [], isLoading } = useQuery('site-config', getSiteConfig);

  const grouped = configs.reduce((acc, config) => {
    const cat = config.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(config);
    return acc;
  }, {} as Record<string, SiteConfig[]>);

  const categoryLabels: Record<string, string> = {
    general: 'Algemeen', theme: 'Thema', branding: 'Branding', contact: 'Contact', seo: 'SEO',
  };

  const handleSave = async (config: SiteConfig) => {
    const value = localValues[config.configKey] ?? config.configValue;
    try {
      setSavingKey(config.configKey);
      await updateSiteConfig(config.configKey, value, config.configType);
      queryClient.invalidateQueries('site-config');
      toast({ title: 'Opgeslagen' });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Opslaan mislukt', description: String(err) });
    } finally { setSavingKey(null); }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AdminSidebar />
        <main className="flex-1 overflow-auto bg-[#f6f6f7]">
          <div className="border-b border-gray-200 bg-white px-8 py-6">
            <div className="max-w-4xl mx-auto flex items-center gap-3">
              <SidebarTrigger className="md:hidden"><Menu className="h-5 w-5" /></SidebarTrigger>
              <div>
                <h1 className="text-xl font-semibold text-[#202223]">Site configuratie</h1>
                <p className="text-sm text-[#6d7175] mt-0.5">Thema, branding en overige site-instellingen</p>
              </div>
            </div>
          </div>

          <div className="px-8 py-8">
            <div className="max-w-4xl mx-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(grouped).map(([category, items]) => (
                    <div key={category} className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] border border-gray-200/60">
                      <div className="px-5 py-4 border-b border-gray-100">
                        <h2 className="text-sm font-semibold text-[#202223]">{categoryLabels[category] || category}</h2>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {items.map((config) => {
                          const val = localValues[config.configKey] ?? config.configValue;
                          return (
                            <div key={config.id} className="px-5 py-4">
                              <div className="flex items-center justify-between mb-1.5">
                                <label className="text-sm font-medium text-[#202223]">{config.label}</label>
                                <button onClick={() => handleSave(config)} disabled={savingKey === config.configKey} className="inline-flex items-center gap-1 text-[13px] text-[#6d7175] hover:text-[#202223] disabled:opacity-50">
                                  {savingKey === config.configKey ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                                  Opslaan
                                </button>
                              </div>
                              {config.description && <p className="text-xs text-[#6d7175] mb-2">{config.description}</p>}
                              {config.configType === 'color' ? (
                                <div className="flex gap-2 items-center">
                                  <input type="color" value={val || '#000000'} onChange={(e) => setLocalValues({ ...localValues, [config.configKey]: e.target.value })} className="w-10 h-9 border border-gray-200 rounded-lg cursor-pointer p-0.5" />
                                  <input type="text" value={val} onChange={(e) => setLocalValues({ ...localValues, [config.configKey]: e.target.value })} className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-gray-300" />
                                </div>
                              ) : config.configType === 'boolean' ? (
                                <label className="flex items-center gap-2">
                                  <input type="checkbox" checked={val === 'true'} onChange={(e) => setLocalValues({ ...localValues, [config.configKey]: String(e.target.checked) })} className="rounded border-gray-300" />
                                  <span className="text-sm text-[#6d7175]">{val === 'true' ? 'Aan' : 'Uit'}</span>
                                </label>
                              ) : (
                                <input type="text" value={val} onChange={(e) => setLocalValues({ ...localValues, [config.configKey]: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-gray-300" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
